import type { TimestampValue } from '@cbm/types';

function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime());
}

function fallbackIsoString(): string {
  return new Date().toISOString();
}

export function toIsoString(value: TimestampValue | undefined | null): string {
  if (!value) {
    return fallbackIsoString();
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isValidDate(parsed) ? parsed.toISOString() : fallbackIsoString();
  }

  if (typeof value === 'number') {
    const milliseconds = value < 1_000_000_000_000 ? value * 1000 : value;
    const parsed = new Date(milliseconds);
    return isValidDate(parsed) ? parsed.toISOString() : fallbackIsoString();
  }

  if (value instanceof Date) {
    return isValidDate(value) ? value.toISOString() : fallbackIsoString();
  }

  if (typeof value === 'object') {
    if ('toDate' in value && typeof value.toDate === 'function') {
      const parsed = value.toDate();
      if (parsed instanceof Date && isValidDate(parsed)) {
        return parsed.toISOString();
      }
    }

    const seconds =
      'seconds' in value && typeof value.seconds === 'number'
        ? value.seconds
        : '_seconds' in value && typeof value._seconds === 'number'
          ? value._seconds
          : undefined;

    if (seconds !== undefined) {
      const parsed = new Date(seconds * 1000);
      return isValidDate(parsed) ? parsed.toISOString() : fallbackIsoString();
    }
  }

  return fallbackIsoString();
}

export function formatTime(value: TimestampValue | undefined | null): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(toIsoString(value)));
}
